import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router, NavigationExtras } from '@angular/router';

// IMPORTAMOS O NOVO SERVIÇO AO INVÉS DO TOASTR ANTIGO
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { AccessDeniedModalService } from 'src/app/shared/services/access-denied-modal.service';
import { IS_AUDIT_FIELD_RESOLUTION } from 'src/app/shared/services/audit-field-resolver.service';
import { resolveHttpErrorMessage } from 'src/app/shared/utils/resolve-http-error-message.util';

@Injectable()
export class HttpAppErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private toastService: AppToastService,
    private accessDeniedModalService: AccessDeniedModalService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Resolução de campo do modal de audit history: o resolver já tem fallback
        // silencioso (catchError -> devolve o id cru) pra qualquer erro, não só 404 —
        // uma FK órfã/de outro tenant pode vir como 500 (ex.: GetClient.cs usa
        // SingleAsync e estoura InvalidOperationException), não só como 404. Tratado
        // antes da cadeia de status pra cobrir todo código de erro, não só o 404.
        if (req.context.get(IS_AUDIT_FIELD_RESOLUTION)) {
          return throwError(error);
        }

        if (error) {
          // Detecta se é a busca de Vessel Finder para não redirecionar em caso de 500
          const isVesselSearch = req.url.includes('vessels/search/');
          const isBillOfLadingSave =
            req.url.includes('billOfLadings') &&
            (req.method === 'POST' || req.method === 'PUT');
          // Detecta busca por IMO no DB (404 é esperado = vessel não cadastrado, não mostrar toast)
          // getVessel agora recebe o Imo via query string (?Imo=...), então distinguimos
          // do getVessel por id verificando o parâmetro, não só o path.
          const isVesselByImo =
            req.url.includes('vessels/getvessel') && req.params.has('Imo');

          if (
            isBillOfLadingSave &&
            (error.status === 400 || error.status === 409 || error.status === 500)
          ) {
            return throwError(error);
          }

          if (error.status === 400) {
            this.handle400Error(error);
          } else if (error.status === 401) {
            this.toastService.error(
              'Unauthorized',
              'Your session has expired or is invalid.',
            );
          }
          // ADICIONADO: 403 - Fim das mensagens feias com a URL exposta
          else if (error.status === 403) {
            this.accessDeniedModalService.open({
              message: resolveHttpErrorMessage(
                error,
                'You do not have permission to access this resource.',
              ),
              permissionName:
                error.error?.permissionName || `${req.method} ${req.url}`,
              permissionKey:
                error.error?.permissionKey || `${req.method}:${req.url}`,
              requestUrl: error.error?.url || req.url,
              method: error.error?.method || req.method,
            });

            // Interrompe propagação do 403 para evitar toasts locais de "Load Error".
            return EMPTY;
          } else if (error.status === 404 && !isVesselByImo) {
            this.toastService.warning(
              'Not Found',
              resolveHttpErrorMessage(
                error,
                'The requested record could not be found.',
              ),
            );
          } else if (error.status === 409) {
            // ADICIONADO: Pega a mensagem do C# (ex: "Port already exists") e joga no Toast
            this.toastService.warning(
              'Conflict',
              resolveHttpErrorMessage(
                error,
                'This record already exists or is in use.',
              ),
            );
          } else if (error.status === 500) {
            if (isVesselSearch || isBillOfLadingSave) {
              return throwError(error);
            }

            const serverError = error.error;

            // ADICIONADO: Captura erros de Banco de Dados (Excluir algo que está em uso)
            // Impede que a tela seja redirecionada para a página de erro do servidor
            // A busca agora ignora case (toLowerCase) para garantir
            const errStr = JSON.stringify(serverError || error).toLowerCase();

            if (
              errStr.includes('reference constraint') ||
              errStr.includes('the delete statement conflicted')
            ) {
              this.toastService.error(
                'Cannot Delete',
                'This record is currently in use by another part of the system.',
              );
            } else if (serverError && serverError.details) {
              // Se for erro real de servidor, manda pra página 500
              const navigationExtras: NavigationExtras = {
                state: { error: serverError },
              };
              this.router.navigateByUrl('/server-error', navigationExtras);
            } else {
              this.toastService.error(
                'Server Error',
                resolveHttpErrorMessage(error, 'An unexpected error occurred.'),
              );
            }
          }
        }

        // Deixa o erro seguir o fluxo caso o componente precise fazer algo (como parar o loading)
        return throwError(error);
      }),
    );
  }

  private handle400Error(error: HttpErrorResponse) {
    if (error.error && error.error.errors) {
      const errorObj = error.error.errors;
      let toastMessage = '';

      for (const key in errorObj) {
        if (errorObj.hasOwnProperty(key)) {
          errorObj[key].forEach((message: string) => {
            // Quebra de linha simples ao invés de HTML para o novo Toast
            toastMessage += `${message}\n`;
          });
        }
      }
      this.toastService.error('Validation Error', toastMessage.trim());
    } else {
      this.toastService.error(
        'Invalid Request',
        resolveHttpErrorMessage(error, error.statusText),
      );
    }
  }
}
